"use client";

import classNames from "classnames";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import styles from "./index.module.css";

export interface WordRotateProps {
  className?: string;
  duration?: number;
  items: ReactNode[];
}

type Stage = "idle" | "exiting" | "beforeEnter";

const ANIMATION_EASING = "cubic-bezier(0.33, 1, 0.68, 1)";

export function WordRotate({
  className = "",
  duration = 2500,
  items,
}: WordRotateProps) {
  const list = useMemo(
    () =>
      Array.isArray(items)
        ? items.filter((n) => n !== null && n !== undefined)
        : [],
    [items]
  );
  const hasItems = list.length > 0;

  const [index, setIndex] = useState(0);
  const [stage, setStage] = useState<Stage>("idle");

  const safeDuration = Math.max(duration, 1);
  const enterMs = 500;
  const exitMs = 500;
  const holdMs = Math.max(safeDuration - enterMs - exitMs, 0);

  const current = hasItems ? list[index % list.length] : null;

  useEffect(() => {
    if (!hasItems || list.length <= 1) {
      return;
    }

    const exitTimer = window.setTimeout(() => {
      setStage("exiting");
    }, enterMs + holdMs);

    const nextTimer = window.setTimeout(() => {
      setStage("beforeEnter");
      setIndex((prev) => (prev + 1) % list.length);
    }, enterMs + holdMs + exitMs);

    return () => {
      window.clearTimeout(exitTimer);
      window.clearTimeout(nextTimer);
    };
  }, [enterMs, exitMs, holdMs, hasItems, list.length, index]);

  useEffect(() => {
    if (!hasItems) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setStage("idle");
    });

    return () => window.cancelAnimationFrame(frame);
  }, [index, hasItems]);

  useEffect(() => {
    if (!hasItems) {
      return;
    }
    setIndex(0);
    setStage("idle");
  }, [hasItems, list.length]);

  if (!hasItems) {
    return null;
  }

  const durationValue = `${enterMs}ms`;
  const wordClass = classNames(styles.word, {
    [styles.wordIdle]: stage === "idle",
    [styles.wordExiting]: stage === "exiting",
    [styles.wordBeforeEnter]: stage === "beforeEnter",
  });

  const wordStyle: CSSProperties = {
    transitionDuration: `${durationValue}, ${durationValue}`,
    transitionTimingFunction: `${ANIMATION_EASING}, ${ANIMATION_EASING}`,
  };

  return (
    <div className={classNames(styles.container, className)} aria-live="polite">
      <span data-word-rotate="active" className={wordClass} style={wordStyle}>
        {current}
      </span>

      {list.map((item, i) => (
        <span className={styles.measure} aria-hidden="true" key={i}>
          {item}
        </span>
      ))}
    </div>
  );
}

export default WordRotate;
